import React, { useState, useMemo, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import axiosInstance from '../utils/axiosConfig';

const SeaCostModal = ({ isOpen, onClose, onApply, fileId, isNewFile = false, initialCostData = null }) => {
    const initialData = {
        shipment_term: '',
        weight: '',
        goods_cost: '',
        exchange_rate: '',
        port_fee: '',
        shipment_sea: '',
        insurance: '',
        container_num: '',
        purchases_tax_value: '',
        customs_value: '',
        d_q_m_value: '',
        office_fee_quantity: '',
        office_fee_value: '',
        diff_clearance_fee_quantity: '',
        diff_clearance_fee_value: '',
        special_clearance_fee_quantity: '',
        special_clearance_fee_value: '',
        port_crossing_storage_fee_quantity: '',
        crane_docs_value: '',
        fs_without_do_quantity: '',
        st_fee_after4d_quantity: '',
        st_fee_after4d_value: '',
        con_dam_fee_quantity: '',
        con_dam_fee_value: '',
        prev_balance_quantity: '',
        prev_balance_value: '',
        cus_war_del_quantity: '',
        cus_war_del_value: ''
    };
    
    const [data, setData] = useState(initialData);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [hasExistingData, setHasExistingData] = useState(false);
    const [error, setError] = useState('');
    const [validationErrors, setValidationErrors] = useState({});
    const [exporting, setExporting] = useState(false);
    const modalRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return;

        if (isNewFile) {
            if (initialCostData) {
                const loadedData = {};
                Object.keys(initialData).forEach(key => {
                    if (initialCostData[key] !== undefined && initialCostData[key] !== null) {
                        loadedData[key] = String(initialCostData[key]);
                    } else {
                        loadedData[key] = '';
                    }
                });
                setData(loadedData);
                setHasExistingData(true);
            } else {
                setData(initialData);
                setHasExistingData(false);
            }
        } else if (fileId) {
            loadExistingData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, fileId, isNewFile, initialCostData]);

    const loadExistingData = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/maritime-cost/${fileId}/`);
            if (response.data && response.data.exists !== false) {
                const loadedData = {};
                Object.keys(initialData).forEach(key => {
                    if (response.data[key] !== undefined && response.data[key] !== null) {
                        loadedData[key] = String(response.data[key]);
                    } else {
                        loadedData[key] = '';
                    }
                });
                setData(loadedData);
                setHasExistingData(true);
            } else {
                setData(initialData);
                setHasExistingData(false);
            }
        } catch (error) {
            console.error('Error loading cost data:', error);
            setData(initialData);
            setHasExistingData(false);
        }
        setLoading(false);
    };

    const handleSaveAndApply = async () => {
        setSaving(true);
        setError('');
        try {
            const apiData = {};
            Object.keys(data).forEach(key => {
                if (key === 'shipment_term') {
                    apiData[key] = data[key] || '';
                } else {
                    apiData[key] = parseFloat(data[key]) || 0;
                }
            });

            const containerNum = parseFloat(data.container_num) || 0;
            apiData.st_fee_after4d_quantity = containerNum;
            apiData.con_dam_fee_quantity = containerNum;

            if (fileId && !isNewFile) {
                await axiosInstance.post(`/maritime-cost/${fileId}/`, apiData);
            }
            
            onApply(costs.total_payment, apiData);
        } catch (error) {
            console.error('Error saving cost data:', error);
            setError('حدث خطأ أثناء حفظ البيانات');
        }
        setSaving(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        // Clear error for this field
        if (validationErrors[name]) {
            setValidationErrors(prev => ({ ...prev, [name]: false }));
        }
        if (error) setError('');
        
        // If it's shipment_term, allow any text
        if (name === 'shipment_term') {
            setData(prev => ({ ...prev, [name]: value }));
            return;
        }
        
        // For numeric fields, remove any non-numeric characters except decimal point
        let cleanValue = value.replace(/[^\d.]/g, '');
        
        // Prevent multiple decimal points
        const parts = cleanValue.split('.');
        if (parts.length > 2) {
            cleanValue = parts[0] + '.' + parts.slice(1).join('');
        }
        
        // Keep the EXACT cleaned value - no conversion
        setData(prev => ({ ...prev, [name]: cleanValue }));
    };

    const costs = useMemo(() => {
        const p = (v) => parseFloat(v) || 0;
        const goods_total = p(data.goods_cost) * p(data.exchange_rate);
        const delivery_order = p(data.container_num) * p(data.crane_docs_value);
        const statement_value = goods_total + delivery_order + p(data.port_fee) + p(data.shipment_sea) + p(data.insurance);
        const purchases_tax_total = p(data.purchases_tax_value) * p(data.weight);
        const customs_total = p(data.customs_value) * statement_value;
        const d_q_m_total = (statement_value + customs_total + purchases_tax_total) * p(data.d_q_m_value);
        const office_fee_total = p(data.office_fee_quantity) * p(data.office_fee_value);
        const diff_clearance_total = p(data.diff_clearance_fee_quantity) * p(data.diff_clearance_fee_value);
        const special_clearance_total = p(data.special_clearance_fee_quantity) * p(data.special_clearance_fee_value);
        const port_crossing_storage_total = p(data.port_crossing_storage_fee_quantity) * p(data.port_fee);
        const crane_docs_total = p(data.container_num) * p(data.crane_docs_value);
        const fs_without_do_total = p(data.fs_without_do_quantity) * p(data.shipment_sea);
        const st_fee_after4d_total = p(data.container_num) * p(data.st_fee_after4d_value);
        const con_dam_fee_total = p(data.container_num) * p(data.con_dam_fee_value);
        const prev_balance_total = p(data.prev_balance_quantity) * p(data.prev_balance_value);
        const cus_war_del_total = p(data.cus_war_del_quantity) * p(data.cus_war_del_value);
        const non_taxable_total = purchases_tax_total + customs_total + d_q_m_total + office_fee_total + 
            port_crossing_storage_total + crane_docs_total + con_dam_fee_total + 
            prev_balance_total + cus_war_del_total + fs_without_do_total;
        const taxable_total = diff_clearance_total + special_clearance_total + st_fee_after4d_total + con_dam_fee_total;
        const taxable_vat = 0.18 * taxable_total;
        const total_payment = non_taxable_total + taxable_total + taxable_vat;

        return {
            goods_total, delivery_order, statement_value, purchases_tax_total, customs_total,
            d_q_m_total, office_fee_total, diff_clearance_total, special_clearance_total,
            port_crossing_storage_total, crane_docs_total, fs_without_do_total, st_fee_after4d_total,
            con_dam_fee_total, prev_balance_total, cus_war_del_total, non_taxable_total,
            taxable_total, taxable_vat, total_payment
        };
    }, [data]);

    const handleExportImage = async () => {
        if (!modalRef.current) return;
        setExporting(true);
        try {
            const original = modalRef.current;
            const clone = original.cloneNode(true);

            const header = clone.querySelector('.modal-header');
            if (header) header.remove();

            const buttonsSection = clone.querySelector('[data-export-hide]');
            if (buttonsSection) buttonsSection.remove();

            const errorDiv = clone.querySelector('[style*="fff5f5"]');
            if (errorDiv) errorDiv.remove();

            // Remove all children except the content div (which is after modal-header)
            const allChildren = Array.from(clone.children);
            allChildren.forEach(child => {
                if (child.classList && child.classList.contains('modal-header')) {
                    child.remove();
                } else {
                    child.style.height = 'auto';
                    child.style.maxHeight = 'none';
                    child.style.overflow = 'visible';
                    child.style.padding = '10px';
                    child.style.paddingBottom = '5px';
                }
            });

            const origInputs = original.querySelectorAll('input, textarea, select');
            const cloneInputs = clone.querySelectorAll('input, textarea, select');
            origInputs.forEach((orig, i) => {
                if (cloneInputs[i]) {
                    cloneInputs[i].setAttribute('value', orig.value);
                    cloneInputs[i].value = orig.value;
                }
            });

            clone.querySelectorAll('*').forEach(el => {
                el.style.transition = 'none';
                el.style.animation = 'none';
            });

            // Remove extra space from all containers
            clone.querySelectorAll('div, table, tbody, tr, td').forEach(el => {
                el.style.marginBottom = '0';
            });

            Object.assign(clone.style, {
                position: 'absolute',
                top: '0',
                left: '-9999px',
                height: 'auto',
                maxHeight: 'none',
                overflow: 'visible',
                width: original.offsetWidth + 'px',
                zIndex: '-1',
                background: '#ffffff',
                opacity: '1',
                transform: 'none',
                animation: 'none',
                transition: 'none',
                borderRadius: '0',
                padding: '0',
            });

            document.body.appendChild(clone);
            await new Promise(resolve => setTimeout(resolve, 150));

            const canvas = await html2canvas(clone, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                logging: false,
            });

            document.body.removeChild(clone);

            const link = document.createElement('a');
            link.download = `sea_cost_${fileId || 'new'}_${new Date().toISOString().slice(0,10)}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        } catch (err) {
            console.error('Export error:', err);
            setError('حدث خطأ أثناء تصدير الصورة');
        }
        setExporting(false);
    };

    const formatNumber = (num) => {
        return (parseFloat(num) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    if (!isOpen) return null;

    const cellStyle = { border: '1px solid #ccc', padding: '4px 8px', textAlign: 'center', fontSize: '11px', height: '24px' };
    const numberCellStyle = { ...cellStyle, direction: 'ltr' };
    const headerCellStyle = { ...cellStyle, background: '#03428d', color: 'white', fontWeight: '600' };
    const inputCellStyle = { ...cellStyle, padding: '0', background: '#fff' };
    const inputStyle = { width: '100%', height: '100%', border: 'none', textAlign: 'center', fontSize: '11px', background: 'transparent', outline: 'none' };
    const inputProps = { autoComplete: 'off', autoCorrect: 'off', autoCapitalize: 'off', spellCheck: 'false' };
    const disabledCellStyle = { ...cellStyle, background: '#e8f4fc', color: '#03428d', fontWeight: '600' };
    const yellowCell = { ...cellStyle, background: '#f8f9fa', textAlign: 'right', paddingRight: '8px', fontSize: '10px', color: '#333', fontWeight: '500' };
    const orangeCell = { ...cellStyle, background: '#03428d', color: 'white' };
    const greenCell = { ...cellStyle, background: '#e8f4fc', color: '#03428d', fontWeight: '600' };
    const summaryLabelStyle = { ...cellStyle, background: '#f8f9fa', color: '#333', textAlign: 'right', paddingRight: '8px', fontWeight: '600' };

    return (
        <div className="modal-overlay" onClick={onClose} style={{ zIndex: 2100 }}>
            <div ref={modalRef} className="modal cost-calculation-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1400px', width: '98vw', height: '98vh', maxHeight: '98vh', padding: 0, overflow: 'hidden', borderRadius: '12px' }}>
                <div className="modal-header" style={{ background: '#03428d', padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '12px 12px 0 0' }}>
                    <h3 style={{ color: 'white', margin: 0, fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 20 20">
                            <path d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm2 10a1 1 0 10-2 0v3a1 1 0 102 0v-3zm2-3a1 1 0 011 1v5a1 1 0 11-2 0v-5a1 1 0 011-1zm4 2a1 1 0 10-2 0v3a1 1 0 102 0v-3z" />
                        </svg>
                        {hasExistingData ? 'عرض/تعديل حسبة ملف بحري/جوي' : 'حساب تكلفة ملف بحري/جوي'}
                    </h3>
                    <button type="button" onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', padding: '5px' }}>
                        <svg fill="currentColor" viewBox="0 0 20 20" style={{ width: '20px', height: '20px' }}>
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>
                
                {loading ? (
                    <div style={{ padding: '3rem', textAlign: 'center' }}>
                        <div style={{ width: '50px', height: '50px', border: '4px solid #e1e8ed', borderTop: '4px solid #03428d', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }}></div>
                        <p style={{ color: '#64748b' }}>جاري تحميل البيانات...</p>
                        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                    </div>
                ) : (
                    <div style={{ padding: '15px', overflow: 'auto', height: 'calc(98vh - 60px)', maxHeight: 'calc(98vh - 60px)' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', direction: 'rtl', tableLayout: 'fixed' }}>
                            <colgroup>
                                <col style={{ width: '7%' }} />
                                <col style={{ width: '10%' }} />
                                <col style={{ width: '8%' }} />
                                <col style={{ width: '10%' }} />
                                <col style={{ width: '8%' }} />
                                <col style={{ width: '10%' }} />
                                <col style={{ width: '10%' }} />
                                <col style={{ width: '9%' }} />
                                <col style={{ width: '9%' }} />
                                <col style={{ width: '7%' }} />
                                <col style={{ width: '12%' }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    <td style={headerCellStyle}>رقم الملف</td>
                                    <td style={headerCellStyle}>شروط الشحن</td>
                                    <td style={headerCellStyle}>الوزن الصافي</td>
                                    <td style={headerCellStyle}>قيمة البضاعة</td>
                                    <td style={headerCellStyle}>سعر الصرف</td>
                                    <td style={headerCellStyle}>المجموع</td>
                                    <td style={headerCellStyle}>دليفري اوردر</td>
                                    <td style={headerCellStyle}>رسوم ميناء</td>
                                    <td style={headerCellStyle}>شحن بحري</td>
                                    <td style={headerCellStyle}>تأمين</td>
                                    <td style={headerCellStyle}>قيمة البيان</td>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={cellStyle}>{fileId || '-'}</td>
                                    <td style={inputCellStyle}><input type="text" name="shipment_term" value={data.shipment_term} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="weight" value={data.weight} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="goods_cost" value={data.goods_cost} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="exchange_rate" value={data.exchange_rate} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={disabledCellStyle}>{Math.round(costs.goods_total)}</td>
                                    <td style={disabledCellStyle}>{Math.round(costs.delivery_order)}</td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="port_fee" value={data.port_fee} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="shipment_sea" value={data.shipment_sea} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="insurance" value={data.insurance} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={disabledCellStyle}>{Math.round(costs.statement_value)}</td>
                                </tr>
                            </tbody>
                        </table>

                        <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginTop: '8px', direction: 'rtl' }}>
                            <div style={{ ...cellStyle, width: '7%', textAlign: 'center' }}>عدد الحاويات</div>
                            <div style={{ ...inputCellStyle, width: '7%' }}><input type="text" inputMode="numeric" name="container_num" value={data.container_num} onChange={handleChange} style={inputStyle} autoComplete="off" /></div>
                        </div>

                        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0', direction: 'rtl', marginTop: '5px', tableLayout: 'fixed' }}>
                            <colgroup>
                                <col style={{ width: '3%' }} />
                                <col style={{ width: '20%' }} />
                                <col style={{ width: '6%' }} />
                                <col style={{ width: '6%' }} />
                                <col style={{ width: '7%' }} />
                                <col style={{ width: '2%' }} />
                                <col style={{ width: '56%' }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    <td style={headerCellStyle}></td>
                                    <td style={headerCellStyle}>التفاصيل</td>
                                    <td style={headerCellStyle}>الكمية</td>
                                    <td style={headerCellStyle}>القيمة</td>
                                    <td style={headerCellStyle}>المجموع</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={{ ...headerCellStyle, background: '#03428d', color: '#fff' }}>شروط المطالبة المالية</td>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={numberCellStyle}>1</td>
                                    <td style={cellStyle}>ضريبة مشتريات</td>
                                    <td style={disabledCellStyle}></td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="purchases_tax_value" value={data.purchases_tax_value} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={disabledCellStyle}>{Math.round(costs.purchases_tax_total)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={yellowCell}>المطالبة المالية هي عبارة عن حسبة تقريبية وليست نهائية قابلة للزيادة او النقصان.</td>
                                </tr>
                                <tr>
                                    <td style={numberCellStyle}>2</td>
                                    <td style={cellStyle}>جمارك</td>
                                    <td style={disabledCellStyle}></td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="customs_value" value={data.customs_value} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={disabledCellStyle}>{Math.round(costs.customs_total)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={yellowCell}>المطالبة المالية غير شاملة أرضيات داخل الميناء او ارضيات مخازن خارج الميناء.</td>
                                </tr>
                                <tr>
                                    <td style={numberCellStyle}>3</td>
                                    <td style={cellStyle}>ض.ق.م</td>
                                    <td style={disabledCellStyle}></td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="d_q_m_value" value={data.d_q_m_value} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={disabledCellStyle}>{Math.round(costs.d_q_m_total)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={yellowCell}>المطالبة المالية غير شاملة اعطال حاوية (ما يزيد عن فترة السماح).</td>
                                </tr>
                                <tr>
                                    <td style={numberCellStyle}>4</td>
                                    <td style={cellStyle}>اتعاب نصار</td>
                                    <td style={inputCellStyle}><input type="text" inputMode="numeric" name="office_fee_quantity" value={data.office_fee_quantity} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="office_fee_value" value={data.office_fee_value} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={disabledCellStyle}>{Math.round(costs.office_fee_total)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={yellowCell}>المطالبة المالية غير شاملة غرامات جمركية او إعادة تقييم لقيمة الفاتورة.</td>
                                </tr>
                                <tr>
                                    <td style={numberCellStyle}>5</td>
                                    <td style={cellStyle}>متفرقات تخليص (حسب الفواتير الصادرة)</td>
                                    <td style={inputCellStyle}><input type="text" inputMode="numeric" name="diff_clearance_fee_quantity" value={data.diff_clearance_fee_quantity} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="diff_clearance_fee_value" value={data.diff_clearance_fee_value} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={disabledCellStyle}>{Math.round(costs.diff_clearance_total)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={yellowCell}>المطالبة المالية غير شاملة تفتيش الحاوية تفتيش رملية او مخزن بأمر جمركي شامل ودقيق (عتالة).</td>
                                </tr>
                                <tr>
                                    <td style={numberCellStyle}>6</td>
                                    <td style={cellStyle}>اتعاب تخليص خاصة (صحة\اتصالات\تيكن)</td>
                                    <td style={inputCellStyle}><input type="text" inputMode="numeric" name="special_clearance_fee_quantity" value={data.special_clearance_fee_quantity} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="special_clearance_fee_value" value={data.special_clearance_fee_value} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={disabledCellStyle}>{Math.round(costs.special_clearance_total)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={yellowCell}>المطالبة المالية غير شاملة أي عملية اتلاف او نقل البضاعة الى المخازن الجمركية وتخزينها.</td>
                                </tr>
                                <tr>
                                    <td style={numberCellStyle}>7</td>
                                    <td style={cellStyle}>رسوم ميناء / معبر / مخزن جزئي</td>
                                    <td style={inputCellStyle}><input type="text" inputMode="numeric" name="port_crossing_storage_fee_quantity" value={data.port_crossing_storage_fee_quantity} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={disabledCellStyle}>{data.port_fee || 0}</td>
                                    <td style={disabledCellStyle}>{Math.round(costs.port_crossing_storage_total)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={yellowCell}>المطالبة المالية غير شاملة نقل الى مخزن الزبون.</td>
                                </tr>
                                <tr>
                                    <td style={numberCellStyle}>8</td>
                                    <td style={cellStyle}>رافعة + استلام اوراق من شركة الشحن</td>
                                    <td style={disabledCellStyle}>{data.container_num || 0}</td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="crane_docs_value" value={data.crane_docs_value} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={disabledCellStyle}>{Math.round(costs.crane_docs_total)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={{ ...cellStyle, background: 'transparent', border: 'none' }}></td>
                                </tr>
                                <tr>
                                    <td style={numberCellStyle}>9</td>
                                    <td style={cellStyle}>قيمة الشحن البحري بدون دلفيري اوردر</td>
                                    <td style={inputCellStyle}><input type="text" inputMode="numeric" name="fs_without_do_quantity" value={data.fs_without_do_quantity} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={disabledCellStyle}>{data.shipment_sea || 0}</td>
                                    <td style={disabledCellStyle}>{Math.round(costs.fs_without_do_total)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={{ ...cellStyle, background: 'transparent', border: 'none' }}></td>
                                </tr>
                                <tr>
                                    <td style={numberCellStyle}>10</td>
                                    <td style={cellStyle}>أجور تخزين ما يزيد عن 4 أيام</td>
                                    <td style={disabledCellStyle}>{data.container_num || 0}</td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="st_fee_after4d_value" value={data.st_fee_after4d_value} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={disabledCellStyle}>{Math.round(costs.st_fee_after4d_total)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={{ ...cellStyle, background: 'transparent', border: 'none' }}></td>
                                </tr>
                                <tr>
                                    <td style={numberCellStyle}>11</td>
                                    <td style={cellStyle}>اعطال حاوية ما يزيد عن العقد</td>
                                    <td style={disabledCellStyle}>{data.container_num || 0}</td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="con_dam_fee_value" value={data.con_dam_fee_value} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={disabledCellStyle}>{Math.round(costs.con_dam_fee_total)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={{ ...cellStyle, background: 'transparent', border: 'none' }}></td>
                                </tr>
                                <tr>
                                    <td style={numberCellStyle}>12</td>
                                    <td style={cellStyle}>رصيد سابق</td>
                                    <td style={inputCellStyle}><input type="text" inputMode="numeric" name="prev_balance_quantity" value={data.prev_balance_quantity} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="prev_balance_value" value={data.prev_balance_value} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={disabledCellStyle}>{Math.round(costs.prev_balance_total)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={{ ...cellStyle, background: 'transparent', border: 'none' }}></td>
                                </tr>
                                <tr>
                                    <td style={numberCellStyle}>13</td>
                                    <td style={cellStyle}>خارج الفاتورة</td>
                                    <td style={inputCellStyle}><input type="text" inputMode="numeric" name="cus_war_del_quantity" value={data.cus_war_del_quantity} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="cus_war_del_value" value={data.cus_war_del_value} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={disabledCellStyle}>{Math.round(costs.cus_war_del_total)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={{ ...cellStyle, background: 'transparent', border: 'none' }}></td>
                                </tr>
                                <tr>
                                    <td colSpan="4" style={summaryLabelStyle}>المجموع الغير خاضع للضريبة</td>
                                    <td style={greenCell}>{Math.round(costs.non_taxable_total)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={{ ...cellStyle, background: 'transparent', border: 'none' }}></td>
                                </tr>
                                <tr>
                                    <td colSpan="4" style={summaryLabelStyle}>المجموع الخاضع للضريبة</td>
                                    <td style={greenCell}>{Math.round(costs.taxable_total)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={{ ...cellStyle, background: 'transparent', border: 'none' }}></td>
                                </tr>
                                <tr>
                                    <td colSpan="3" style={summaryLabelStyle}>ض.ق.م</td>
                                    <td style={greenCell}>0.18</td>
                                    <td style={greenCell}>{Math.round(costs.taxable_vat)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={{ ...cellStyle, background: 'transparent', border: 'none' }}></td>
                                </tr>
                                <tr>
                                    <td colSpan="4" style={{ ...summaryLabelStyle }}>المجموع للدفع</td>
                                    <td style={{ ...greenCell, fontSize: '12px' }}>{Math.floor(costs.total_payment || 0)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={{ ...cellStyle, background: 'transparent', border: 'none' }}></td>
                                </tr>
                            </tbody>
                        </table>

                        {error && (
                            <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '8px', background: '#fff5f5', border: '1px solid #dc3545', borderRadius: '4px', color: '#dc3545', fontSize: '12px' }}>
                                <span style={{ fontWeight: '600' }}>{error}</span>
                            </div>
                        )}

                        <div data-export-hide="true" style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '15px', paddingBottom: '10px', background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginLeft: '5px', marginRight: '5px' }}>
                            <button 
                                type="button" 
                                onClick={handleSaveAndApply} 
                                disabled={saving}
                                style={{ 
                                    padding: '10px 25px', 
                                    fontSize: '14px', 
                                    fontWeight: '600', 
                                    background: '#03428d', 
                                    color: 'white', 
                                    border: 'none', 
                                    borderRadius: '8px', 
                                    cursor: 'pointer', 
                                    opacity: saving ? 0.7 : 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {saving ? (
                                    <>
                                        <div style={{ width: '16px', height: '16px', border: '2px solid #fff', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                                        <span>جاري الحفظ...</span>
                                    </>
                                ) : (
                                    <>
                                        <svg style={{ width: '18px', height: '18px' }} fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        <span>{isNewFile ? 'تطبيق' : (hasExistingData ? 'حفظ وتطبيق' : 'حفظ التكلفة')}</span>
                                    </>
                                )}
                            </button>
                            <button 
                                type="button" 
                                onClick={onClose} 
                                disabled={saving}
                                style={{ 
                                    padding: '10px 25px', 
                                    fontSize: '14px', 
                                    fontWeight: '600', 
                                    background: 'white', 
                                    color: '#333', 
                                    border: '2px solid #ddd', 
                                    borderRadius: '8px', 
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                <svg style={{ width: '18px', height: '18px' }} fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                                <span>إلغاء</span>
                            </button>
                            <button 
                                type="button" 
                                onClick={handleExportImage} 
                                disabled={exporting}
                                style={{ 
                                    padding: '10px 25px', 
                                    fontSize: '14px', 
                                    fontWeight: '600', 
                                    background: 'linear-gradient(135deg, #2d8659 0%, #3da86e 100%)', 
                                    color: 'white', 
                                    border: 'none', 
                                    borderRadius: '8px', 
                                    cursor: 'pointer', 
                                    opacity: exporting ? 0.7 : 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {exporting ? (
                                    <>
                                        <div style={{ width: '16px', height: '16px', border: '2px solid #fff', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                                        <span>جاري التصدير...</span>
                                    </>
                                ) : (
                                    <>
                                        <svg style={{ width: '18px', height: '18px' }} fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                                        </svg>
                                        <span>تصدير صورة</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default React.memo(SeaCostModal);
